// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/AnswerNode.js (149 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react import swap only.
 *
 * NOTE: AnswerSettingsModal is ported in T3 with the medium/large nodes,
 * so this file imports it from '../Helpers/AnswerSettingsModal' which will
 * exist after T3 lands. Until then this file will fail to render (import
 * error) — build still passes because AnswerSettingsModal is only
 * referenced, not called at module load.
 */
import React, { useContext, useState } from 'react';
import { Handle, useReactFlow } from '@xyflow/react';
import { NodesContext } from '../provider/IvrContext';
import AnswerSettingsModal from '../Helpers/AnswerSettingsModal';
import { toast } from 'sonner';
import {
  PhoneIncoming,
  Settings,
  CheckCircle2,
  Globe,
  Mic,
} from 'lucide-react';
import { triggerHandleConnect } from '../Helpers/utils';

const COLOR = '#059669';

const AnswerNode = ({ data, id }: any) => {
  const { nodeConnections, webhookList, smsWebhookList, updateNodeData } =
    useContext(NodesContext) as any;
  const { getNodes, getEdges } = useReactFlow();
  const outgoingCount = nodeConnections?.['new-target-handle']?.length ?? 0;
  const disabled = outgoingCount >= 1;
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Once a Language node has a child node wired to it, the call flow already
  // depends on the language set configured here — changing languages after
  // that would silently desync the built-out branch, so lock the section.
  const checkLanguagesLocked = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const languageNodeIds = nodes
      .filter((n: any) => n.type === 'languageNode')
      .map((n: any) => n.id);
    return languageNodeIds.some((nodeId: any) =>
      edges.some((e: any) => e.source === nodeId),
    );
  };

  const webhookConfig = data?.webhookConfig || null;
  const selectedLanguages = Array.isArray(data?.languages) ? data.languages : [];
  const ttsProfileId = data?.ttsProfileId || '';
  const ttsProfileLabel = data?.ttsProfile
    ? data.ttsProfile.flow_name || `Profile #${data.ttsProfile.id}`
    : '';

  const collectActionLanguages = (action: any, codes: any) => {
    if (!action || typeof action !== 'object') return;
    if (action.language) codes.add(action.language);
    if (Array.isArray(action.languages)) {
      action.languages.forEach((l: any) => {
        if (l?.language) codes.add(l.language);
      });
    }
    Object.values(action).forEach((val: any) => {
      if (
        val &&
        typeof val === 'object' &&
        !Array.isArray(val) &&
        val.language
      ) {
        codes.add(val.language);
      }
    });
  };

  const collectNodeLanguages = (d: any) => {
    const codes = new Set();
    collectActionLanguages(d?.action, codes);
    collectActionLanguages(d?.defaultAction?.action, codes);
    if (Array.isArray(d?.selectedLanguages)) {
      d.selectedLanguages.forEach((code: any) => {
        if (code) codes.add(code);
      });
    }
    if (d?.keyLangMap && typeof d.keyLangMap === 'object') {
      Object.values(d.keyLangMap).forEach((code: any) => {
        if (code) codes.add(code);
      });
    }
    if (d?.variableTtsConfig && typeof d.variableTtsConfig === 'object') {
      Object.values(d.variableTtsConfig).forEach((cfg: any) => {
        if (cfg?.language) codes.add(cfg.language);
      });
    }
    return codes;
  };

  const isLanguageInUse = (langCode: any) => {
    return getNodes().some((n: any) => collectNodeLanguages(n.data).has(langCode));
  };

  const handleSave = ({
    webhookConfig: wh,
    languages,
    languageNames,
    ttsProfileId: newTtsProfileId,
    ttsProfile,
  }: any) => {
    updateNodeData(id, {
      webhookConfig: wh,
      languages,
      languageNames,
      ttsProfileId: newTtsProfileId,
      ttsProfile,
    });
    toast.success('Answer node settings saved.');
  };

  return (
    <div className="ivrn" style={{ '--node-color': COLOR } as any}>
      <span
        className="ivrn-icon"
        style={{ background: COLOR, color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <PhoneIncoming size={22} strokeWidth={2} />
      </span>
      <Handle
        type="source"
        position={'right' as any}
        id="new-target-handle"
        style={{ background: COLOR }}
        isConnectable={!disabled}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Answer</span>
        <span className="ivrn-badge ivrn-badge--green">START</span>
      </div>

      <div className="ivrn-body">
        {webhookConfig ? (
          <span className="ivrn-val">
            <CheckCircle2
              size={10}
              style={{ color: COLOR, marginRight: 4, flexShrink: 0 }}
            />
            Webhook linked
          </span>
        ) : null}
        {selectedLanguages.length > 0 ? (
          <span className="ivrn-val">
            <Globe
              size={10}
              style={{ color: COLOR, marginRight: 4, flexShrink: 0 }}
            />
            {selectedLanguages.length} language
            {selectedLanguages.length > 1 ? 's' : ''} selected
          </span>
        ) : null}
        {ttsProfileLabel ? (
          <span className="ivrn-val">
            <Mic
              size={10}
              style={{ color: COLOR, marginRight: 4, flexShrink: 0 }}
            />
            TTS: {ttsProfileLabel}
          </span>
        ) : null}
        {!webhookConfig &&
          selectedLanguages.length === 0 &&
          !ttsProfileLabel && (
            <span className="ivrn-hint">Incoming call entry point</span>
          )}
      </div>

      <div className="ivrn-actions">
        <button
          className={`ivrn-btn ivrn-btn--link${
            webhookConfig || selectedLanguages.length > 0 || ttsProfileLabel
              ? ' active'
              : ''
          }`}
          title="Settings"
          onClick={() => setSettingsOpen(true)}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
      </div>

      <AnswerSettingsModal
        show={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        webhookConfig={webhookConfig}
        languages={selectedLanguages}
        ttsProfileId={ttsProfileId}
        onSave={handleSave}
        webhookList={webhookList}
        smsWebhookList={smsWebhookList}
        isLanguageInUse={isLanguageInUse}
        languagesLocked={settingsOpen ? checkLanguagesLocked() : false}
      />
    </div>
  );
};

export default AnswerNode;
